import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { FormHeader } from "@/components/FormHeader";
import { FormCover } from "@/components/FormCover";
import { FormattedCopyMarkdown } from "@/components/form-copy/FormattedCopyMarkdown";
import { cn } from "@/lib/utils";
import type { FormTheme } from "@kushya/shared";

export interface IntroScreenProps {
  alignment: "left" | "center" | "right";
  logoUrl?: string;
  formName?: string;
  theme?: FormTheme;
  introductionCopy?: string | null;
  estimatedMinutes?: number;
  onStart: () => void;
  startLabel?: string;
  hasDraft?: boolean;
  draftUpdatedAt?: string | null;
  onResumeDraft?: () => void;
  onDiscardDraft?: () => void;
}

export function IntroScreen({
  alignment,
  logoUrl,
  formName,
  theme,
  introductionCopy,
  estimatedMinutes,
  onStart,
  startLabel = "Start",
  hasDraft,
  draftUpdatedAt,
  onResumeDraft,
  onDiscardDraft,
}: IntroScreenProps) {
  const formattedDraftUpdatedAt =
    hasDraft && draftUpdatedAt
      ? new Date(draftUpdatedAt).toLocaleString()
      : null;
  const introContent = introductionCopy?.trim() || "Welcome";
  const contentAlignmentClass =
    alignment === "center"
      ? "text-center"
      : alignment === "right"
        ? "text-right"
        : "text-left";
  const actionsAlignmentClass =
    alignment === "center"
      ? "md:justify-center"
      : alignment === "right"
        ? "md:justify-end"
        : "md:justify-start";
  const primaryActionAlignmentClass =
    alignment === "center"
      ? "md:mx-auto"
      : alignment === "right"
        ? "md:ml-auto"
        : "";

  return (
    <div className="form-shell">
      <div className="mx-auto max-w-2xl">
        <FormHeader
          logoUrl={logoUrl}
          formName={formName}
          alignment={alignment}
        />
        <Card className="form-paper p-8 md:p-12">
          <FormCover theme={theme} alignment={alignment} />
          <div className={cn("form-step space-y-4", contentAlignmentClass)}>
            <div className="space-y-2">
              <FormattedCopyMarkdown
                content={introContent}
                variant="intro"
                className="form-heading"
              />
              {/* <p className="text-sm text-muted-foreground">
                This form includes {totalQuestions} question
                {totalQuestions === 1 ? "" : "s"}.
              </p> */}
            </div>
            {estimatedMinutes ? (
              <p className="text-sm text-muted-foreground">
                Estimated completion time: ~{estimatedMinutes} minute
                {estimatedMinutes === 1 ? "" : "s"}.
              </p>
            ) : null}
            {hasDraft ? (
              <div className="space-y-3">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-foreground">
                    You have an unfinished response.
                  </p>
                  {formattedDraftUpdatedAt ? (
                    <p className="text-sm text-muted-foreground">
                      Last updated: {formattedDraftUpdatedAt}
                    </p>
                  ) : null}
                </div>
                <div
                  className={cn(
                    "flex flex-col gap-3 md:flex-row",
                    actionsAlignmentClass,
                  )}
                >
                  <Button
                    onClick={onResumeDraft}
                    size="lg"
                    className="w-full md:w-auto"
                    disabled={!onResumeDraft}
                  >
                    Resume
                  </Button>
                  <Button
                    onClick={onDiscardDraft}
                    size="lg"
                    variant="outline"
                    className="w-full md:w-auto"
                    disabled={!onDiscardDraft}
                  >
                    Start over
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                onClick={onStart}
                size="lg"
                className={cn("w-full md:w-auto", primaryActionAlignmentClass)}
              >
                {startLabel}
              </Button>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
